import React from 'react';
import type { OrderlineView } from '../../../../../services/orderlines/orderlines.service';
import { ItemDiscountModal } from '../../receipt/actions/item-discount-modal/ItemDiscountModal';
import { ItemDiscountSelectModal } from '../../receipt/actions/item-discount-select-modal/ItemDiscountSelectModal';
import { isReceiptCancelableOrderline } from '../../../core/order-cancel-flow';
import { getMaxPerUnitDiscount, hasExistingItemDiscount } from '../shared/discount.utils';
import type { OrderItemActionsFlowController } from './useOrderItemActionsFlow';

export const REMOVE_DISCOUNT_DISABLED_MESSAGE = 'על מנת להסיר את ההנחה יש לפנות למנהל';

export type OrderItemActionsModalsProps = {
  flow: OrderItemActionsFlowController;
};

// Summary: Renders the shared item-actions steps for a useOrderItemActionsFlow controller.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-orderssection
export const OrderItemActionsModals: React.FC<OrderItemActionsModalsProps> = ({ flow }) => {
  const isSelectableLine = (line: OrderlineView) => line.status === 'sent'
    || line.status === 'skipped'
    || (flow.isTakeAway && line.status === 'pending');
  const isRowDisabled = (line: OrderlineView) => !flow.isManager
    && line.unitPrice * line.quantity - (line.discountInput ?? 0) <= 0;

  return (
    <>
      <ItemDiscountSelectModal
        isOpen={flow.step === 'itemSelect'}
        title="הנחה לפריטים"
        confirmLabel="המשך"
        hideCloseButton
        orderlines={flow.orderlines}
        isSelectableLine={isSelectableLine}
        isRowDisabled={isRowDisabled}
        onClose={flow.dismiss}
        onConfirm={flow.confirmItemSelect}
      />

      <ItemDiscountSelectModal
        isOpen={flow.step === 'oth'}
        title="על חשבון הבית"
        confirmLabel="אישור"
        showSteps={false}
        hideCloseButton
        orderlines={flow.orderlines}
        isSelectableLine={isSelectableLine}
        isRowDisabled={isRowDisabled}
        onClose={flow.dismiss}
        onConfirm={(selections) => flow.confirmOth(selections)}
      />

      <ItemDiscountSelectModal
        isOpen={flow.step === 'cancel'}
        title="ביטול פריטים"
        confirmLabel="אישור"
        showSteps={false}
        hideCloseButton
        orderlines={flow.orderlines}
        isSelectableLine={isReceiptCancelableOrderline}
        emptyMessage="אין פריטים שנשלחו לביטול."
        isSubmitting={flow.isSaving}
        onClose={flow.dismiss}
        onConfirm={(selections) => flow.confirmCancel(selections)}
      />

      <ItemDiscountModal
        isOpen={flow.step === 'itemDiscount'}
        itemsSubtotal={flow.selectionSubtotal}
        maxDiscountAmount={getMaxPerUnitDiscount(flow.selections, flow.orderlines)}
        hasExistingDiscount={hasExistingItemDiscount(flow.selections, flow.orderlines)}
        isSaving={flow.isSaving}
        onClose={flow.dismiss}
        onBack={flow.backToItemSelect}
        removeDisabledMessage={flow.isManager ? undefined : REMOVE_DISCOUNT_DISABLED_MESSAGE}
        onRemoveDiscount={() => { void flow.removeItemDiscount(); }}
        onConfirm={flow.confirmItemDiscount}
      />
    </>
  );
};
